import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError, type RequestListRow } from '../api';
import { Alert, Badge, Card, EmptyState, PageHeader, Spinner } from '../components/ui';
import { IconCheckCircle } from '../components/icons';
import { REQUEST_STATUS } from './MyRequests';

/**
 * The whole of what an approved-requests viewer can see: every request that
 * has passed approval, and a way into each one's read-only detail screen. No
 * actions here and none on the detail, by role; the API refuses them anyway.
 */
export function ApprovedRequests() {
  const requests = useQuery({ queryKey: ['requests', 'approved'], queryFn: () => api.listRequests('approved') });

  if (requests.isPending) return <Spinner label="Loading approved requests" />;
  if (requests.isError) {
    return <Alert>{requests.error instanceof ApiError ? requests.error.message : 'Could not load requests.'}</Alert>;
  }

  const rows = requests.data.requests;

  return (
    <div>
      <PageHeader
        title="Approved requests"
        description="Every request that has been approved, whether it is still with IT or already completed. Open one to read it and its signed record."
      />

      {rows.length === 0 ? (
        <EmptyState
          icon={<IconCheckCircle />}
          title="Nothing approved yet"
          body="Requests appear here the moment an approver signs them off, and stay here once IT has completed them."
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-3xl text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/60 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400">
                  <th className="px-5 py-3">Reference</th>
                  <th className="px-5 py-3">Employee</th>
                  <th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Department</th>
                  <th className="px-5 py-3">Raised by</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Row key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function Row({ row }: { row: RequestListRow }) {
  const status = REQUEST_STATUS[row.status];
  return (
    <tr className="border-b border-slate-100 transition-colors last:border-b-0 hover:bg-slate-50/70 dark:border-slate-800/70 dark:hover:bg-slate-800/40">
      <td className="px-5 py-3">
        <Link
          to={`/requests/${row.id}`}
          className="font-medium text-brand-700 underline-offset-4 hover:underline dark:text-brand-300"
        >
          {row.reference}
        </Link>
      </td>
      <td className="px-5 py-3 text-slate-900 dark:text-slate-100">
        {row.employeeName || <span className="text-slate-500 dark:text-slate-400">Not named</span>}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">
        {row.type === 'ENROLMENT' ? 'Enrolment' : 'Offboarding'}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">{row.department ?? 'N/A'}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">{row.requestedBy}</td>
      <td className="px-5 py-3">
        <Badge tone={status.tone} dot>
          {status.label}
        </Badge>
      </td>
      <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
        {new Date(row.updatedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
      </td>
    </tr>
  );
}
